import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaEdit,
  FaTrash,
  FaSearch,
  FaBuilding,
  FaPhone,
  FaEnvelope,
  FaMapMarkerAlt,
  FaIdCard,
  FaCheckCircle,
  FaUsers,
  FaUser,
  FaTimes,
  FaCreditCard,
  FaFileContract,
  FaRegFileAlt,
  FaChartLine,
  FaShieldAlt,
  FaMoneyBillWave,
  FaHandshake,
  FaPlus,
  FaEye,
  FaEyeSlash,
  FaInfoCircle,
  FaTag,
  FaPercent,
} from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const AccountManagement = () => {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    companyid: '',
    companyname: '',
    companyaddress: '',
    companyemail: '',
    companyphone: '',
    accountnumber: '',
    is_phone_verified: false,
    is_companyid_verified: false,
    has_contract: false,
    contractid: '',
    gstnumber: '',
    discount_percentage: '',
  });
  const [editCompany, setEditCompany] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 20;
  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({});
  const [editErrors, setEditErrors] = useState({});
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const companiesResponse = await fetch(`${BQ_BASE_URL}/bq/api/companies`);
        const companiesData = await companiesResponse.json();

        if (companiesData && companiesData.companies) {
          setCompanies(companiesData.companies);
        } else {
          toast.error('No corporate companies found.');
        }
      } catch (error) {
        toast.error('Failed to fetch data.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Calculate statistics
  const totalCompanies = companies.length;
  const verifiedCompanies = companies.filter((company) => company.is_fully_verified).length;
  const companiesWithContract = companies.filter((company) => company.has_contract).length;
  const activeCompanies = companies.filter((company) => company.is_active !== false).length;

  // Get discount percentage from contract ID
  const getDiscountFromContractId = (contractId) => {
    if (!contractId) return null;
    const match = contractId.match(/PECDIS_(\d{2,3})/);
    if (match) {
      const discount = parseInt(match[1]);
      if (discount >= 1 && discount <= 50) {
        return discount;
      }
    }
    return null;
  };

  // Validation - Only for GST, Company Name (allow numbers), Company ID
  const validateGST = (gst) => {
    if (!gst) return true;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
    return gstRegex.test(gst);
  };

  const validateCompanyID = (companyId) => {
    if (!companyId) return true;
    // Allow letters, numbers, and underscores
    const validCharsRegex = /^[A-Z0-9_]+$/;
    if (!validCharsRegex.test(companyId)) {
      return 'must_be_valid';
    }
    if (companyId.length < 3 || companyId.length > 20) {
      return 'length_error';
    }
    return true;
  };

  const validateCompanyName = (name) => {
    if (!name) return true;
    // Allow letters, numbers, spaces, and common special characters
    const validCharsRegex = /^[A-Za-z0-9\s\-&',.()]+$/;
    if (!validCharsRegex.test(name)) {
      return 'must_be_valid';
    }
    if (name.length < 2 || name.length > 100) {
      return 'length_error';
    }
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Convert to uppercase for specific fields
    let processedValue = value;
    if (name === 'companyid') {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9_]/g, '');
    }
    if (name === 'gstnumber') {
      processedValue = value.toUpperCase();
    }

    setNewCompany((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
    }));

    // Live validation errors
    if (name === 'companyid') {
      if (!processedValue || processedValue.trim() === '') {
        setErrors((prev) => ({ ...prev, [name]: 'Corporate ID is required' }));
      } else if (!/^[A-Z0-9_]+$/.test(processedValue)) {
        setErrors((prev) => ({
          ...prev,
          [name]: 'Corporate ID must contain only capital letters, numbers, and underscores',
        }));
      } else if (processedValue.length < 3 || processedValue.length > 20) {
        setErrors((prev) => ({ ...prev, [name]: 'Corporate ID must be 3-20 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (name === 'companyname') {
      if (!processedValue || processedValue.trim() === '') {
        setErrors((prev) => ({ ...prev, [name]: 'Company Name is required' }));
      } else if (!/^[A-Za-z0-9\s\-&',.()]+$/.test(processedValue)) {
        setErrors((prev) => ({
          ...prev,
          [name]: 'Company Name can contain letters, numbers, spaces, and basic punctuation',
        }));
      } else if (processedValue.length < 2 || processedValue.length > 100) {
        setErrors((prev) => ({ ...prev, [name]: 'Company Name must be 2-100 characters' }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (name === 'gstnumber') {
      if (!processedValue || processedValue.trim() === '') {
        setErrors((prev) => ({ ...prev, [name]: 'GST Number is required' }));
      } else if (!validateGST(processedValue)) {
        setErrors((prev) => ({ ...prev, [name]: 'Invalid GST format. Example: 22AAAAA0000A1Z5' }));
      } else {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (name === 'contractid') {
      const discount = getDiscountFromContractId(processedValue);
      setNewCompany((prev) => ({
        ...prev,
        discount_percentage: discount || '',
      }));
    }

    if (errors[name] && name !== 'companyid' && name !== 'companyname' && name !== 'gstnumber') {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Convert to uppercase for specific fields
    let processedValue = value;
    if (name === 'gstnumber') {
      processedValue = value.toUpperCase();
    }

    setEditCompany((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : processedValue }));

    // Live validation errors for edit
    if (name === 'companyname') {
      if (!processedValue || processedValue.trim() === '') {
        setEditErrors((prev) => ({ ...prev, [name]: 'Company Name is required' }));
      } else if (!/^[A-Za-z0-9\s\-&',.()]+$/.test(processedValue)) {
        setEditErrors((prev) => ({
          ...prev,
          [name]: 'Company Name can contain letters, numbers, spaces, and basic punctuation',
        }));
      } else if (processedValue.length < 2 || processedValue.length > 100) {
        setEditErrors((prev) => ({ ...prev, [name]: 'Company Name must be 2-100 characters' }));
      } else {
        setEditErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (name === 'gstnumber') {
      if (!processedValue || processedValue.trim() === '') {
        setEditErrors((prev) => ({ ...prev, [name]: 'GST Number is required' }));
      } else if (!validateGST(processedValue)) {
        setEditErrors((prev) => ({
          ...prev,
          [name]: 'Invalid GST format. Example: 22AAAAA0000A1Z5',
        }));
      } else {
        setEditErrors((prev) => ({ ...prev, [name]: '' }));
      }
    }

    if (name === 'contractid') {
      const discount = getDiscountFromContractId(processedValue);
      setEditCompany((prev) => ({
        ...prev,
        discount_percentage: discount || '',
      }));
    }

    if (editErrors[name] && name !== 'companyname' && name !== 'gstnumber') {
      setEditErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (formData) => {
    const newErrors = {};

    // Check for empty required fields
    if (!formData.companyid || formData.companyid.trim() === '') {
      newErrors.companyid = 'Corporate ID is required';
    } else if (!/^[A-Z0-9_]+$/.test(formData.companyid)) {
      newErrors.companyid =
        'Corporate ID must contain only capital letters, numbers, and underscores';
    } else if (formData.companyid.length < 3 || formData.companyid.length > 20) {
      newErrors.companyid = 'Corporate ID must be 3-20 characters';
    }

    if (!formData.companyname || formData.companyname.trim() === '') {
      newErrors.companyname = 'Company Name is required';
    } else if (!/^[A-Za-z0-9\s\-&',.()]+$/.test(formData.companyname)) {
      newErrors.companyname =
        'Company Name can contain letters, numbers, spaces, and basic punctuation';
    } else if (formData.companyname.length < 2 || formData.companyname.length > 100) {
      newErrors.companyname = 'Company Name must be 2-100 characters';
    }

    if (!formData.gstnumber || formData.gstnumber.trim() === '') {
      newErrors.gstnumber = 'GST Number is required';
    } else if (!validateGST(formData.gstnumber)) {
      newErrors.gstnumber = 'Invalid GST format. Example: 22AAAAA0000A1Z5';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm(newCompany);
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);

      // Show specific toast messages for each error
      if (formErrors.companyid) {
        toast.error(formErrors.companyid);
      } else if (formErrors.companyname) {
        toast.error(formErrors.companyname);
      } else if (formErrors.gstnumber) {
        toast.error(formErrors.gstnumber);
      } else {
        toast.error('Please fix the errors in the form');
      }
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/company`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(newCompany),
      });

      const data = await response.json();

      if (data.message === 'Company created successfully') {
        toast.success('Corporate company account created successfully!');
        setCompanies((prevCompanies) => [...prevCompanies, data.company]);
        setShowAddModal(false);
        setNewCompany({
          companyid: '',
          companyname: '',
          companyaddress: '',
          companyemail: '',
          companyphone: '',
          accountnumber: '',
          is_phone_verified: false,
          is_companyid_verified: false,
          has_contract: false,
          contractid: '',
          gstnumber: '',
          discount_percentage: '',
        });
        setErrors({});
      } else {
        toast.error(data.message || 'Failed to create corporate account.');
      }
    } catch (error) {
      toast.error('Error submitting data.');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();

    const formErrors = validateForm(editCompany);
    if (Object.keys(formErrors).length > 0) {
      setEditErrors(formErrors);

      // Show specific toast messages for each error
      if (formErrors.companyid) {
        toast.error(formErrors.companyid);
      } else if (formErrors.companyname) {
        toast.error(formErrors.companyname);
      } else if (formErrors.gstnumber) {
        toast.error(formErrors.gstnumber);
      } else {
        toast.error('Please fix the errors in the form');
      }
      return;
    }

    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/company/${editCompany.companyid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify(editCompany),
      });

      const data = await response.json();

      if (data.message === 'Company updated successfully') {
        toast.success('Corporate company account updated successfully!');
        setCompanies((prevCompanies) =>
          prevCompanies.map((company) =>
            company.companyid === editCompany.companyid ? data.company : company
          )
        );
        setShowEditModal(false);
        setEditErrors({});
      } else {
        toast.error(data.message || 'Failed to update corporate account.');
      }
    } catch (error) {
      toast.error('Error updating data.');
    }
  };

  const handleEditClick = (company) => {
    setEditCompany(company);
    setEditErrors({});
    setShowEditModal(true);
  };

  const handleDelete = async () => {
    try {
      const response = await fetch(`${BQ_BASE_URL}/bq/api/company/${itemToDelete.companyid}`, {
        method: 'DELETE',
        headers: {
          Accept: 'application/json',
        },
      });

      const data = await response.json();

      if (data.message === 'Company deleted successfully') {
        toast.success('Corporate company account deleted successfully!');
        setCompanies((prevCompanies) =>
          prevCompanies.filter((company) => company.companyid !== itemToDelete.companyid)
        );
      } else {
        toast.error(data.message || 'Failed to delete corporate account.');
      }
      setShowDeleteModal(false);
    } catch (error) {
      toast.error('Error deleting item.');
    }
  };

  const filteredCompanies = companies.filter(
    (company) =>
      company.companyid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      company.companyname?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalPages = Math.ceil(filteredCompanies.length / rowsPerPage);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredCompanies.slice(indexOfFirstRow, indexOfLastRow);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen w-full overflow-x-hidden">
      {/* Header Navigation - KEPT AS IS */}
      <div className="flex justify-between items-center mb-6 bg-white p-3 rounded-lg shadow-sm">
        <h1 className="text-black text-xl font-bold">Customer Management</h1>
        <div className="flex space-x-4">
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
            className="text-gray-600 hover:text-black transition"
          >
            Overview
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
            className="text-gray-600 hover:text-black transition"
          >
            Guest Management
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
            className="text-black font-bold"
          >
            Corporate Accounts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
            className="text-gray-600 hover:text-black transition"
          >
            Contracts
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
            className="text-gray-600 hover:text-black transition"
          >
            Credit & Verification
          </button>
          <button
            onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
            className="text-gray-600 hover:text-black transition"
          >
            Settings
          </button>
        </div>
      </div>

      {/* Title Section - KEPT AS IS */}
      <div className="mb-8">
        <div className="flex flex-col items-center justify-center text-center space-y-2">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Corporate Company Management
          </h1>
          <p className="text-gray-600 text-sm max-w-2xl">
            Manage corporate company accounts for employee hotel stays and pricing agreements
          </p>
        </div>
      </div>

      {/* Stats Cards - Clean & Simple */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Companies</p>
              <p className="text-2xl font-bold text-gray-900">{totalCompanies}</p>
            </div>
            <div className="p-2 bg-blue-100 rounded-lg">
              <FaBuilding className="text-blue-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Fully Verified</p>
              <p className="text-2xl font-bold text-gray-900">{verifiedCompanies}</p>
            </div>
            <div className="p-2 bg-green-100 rounded-lg">
              <FaShieldAlt className="text-green-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Contracts</p>
              <p className="text-2xl font-bold text-gray-900">{companiesWithContract}</p>
            </div>
            <div className="p-2 bg-purple-100 rounded-lg">
              <FaHandshake className="text-purple-600 text-lg" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Companies</p>
              <p className="text-2xl font-bold text-gray-900">{activeCompanies}</p>
            </div>
            <div className="p-2 bg-emerald-100 rounded-lg">
              <FaChartLine className="text-emerald-600 text-lg" />
            </div>
          </div>
        </div>
      </div>

      {/* Search and Add Button */}
      <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
        <div className="relative w-full">
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by Corporate ID or Company Name..."
            className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button
          className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center gap-2 shadow-sm whitespace-nowrap"
          onClick={() => {
            setShowAddModal(true);
            setErrors({});
          }}
        >
          <FaPlus className="text-sm" />
          <span>Add Corporate Company</span>
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Corporate ID
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Company Name
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  GST
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Contract
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="text-center py-12">
                    <div className="flex justify-center items-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
                      <span className="ml-3 text-gray-600">Loading corporate companies...</span>
                    </div>
                  </td>
                </tr>
              ) : currentRows.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center py-12 text-gray-500">
                    <FaBuilding className="mx-auto text-4xl text-gray-300 mb-3" />
                    <p>No corporate companies found</p>
                    {searchTerm && (
                      <p className="text-sm mt-1">Try adjusting your search criteria</p>
                    )}
                  </td>
                </tr>
              ) : (
                currentRows.map((company, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition duration-150">
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                        {company.companyid}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <FaBuilding className="text-gray-400 text-sm" />
                        <span className="text-gray-900">{company.companyname}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="space-y-0.5">
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <FaEnvelope className="text-gray-400 text-xs" />
                          <span>{company.companyemail}</span>
                        </div>
                        <div className="flex items-center gap-1 text-xs text-gray-600">
                          <FaPhone className="text-gray-400 text-xs" />
                          <span>{company.companyphone}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {company.gstnumber ? (
                        <span className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded">
                          {company.gstnumber}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-700">-</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {company.has_contract ? (
                        <div>
                          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-green-100 text-green-800">
                            <FaCheckCircle className="mr-1 text-xs" />
                            Active
                          </span>
                          {company.contractid && (
                            <span className="text-xs text-gray-500 block mt-0.5 font-mono">
                              {company.contractid}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                          No Contract
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${
                          company.is_fully_verified
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {company.is_fully_verified ? 'Verified' : 'Pending'}
                      </span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditClick(company)}
                          className="text-gray-600 hover:text-black transition p-1.5 rounded hover:bg-gray-100"
                          title="Edit Company"
                        >
                          <FaEdit size={16} />
                        </button>
                        <button
                          onClick={() => {
                            setItemToDelete(company);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-500 hover:text-red-700 transition p-1.5 rounded hover:bg-red-50"
                          title="Delete Company"
                        >
                          <FaTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {filteredCompanies.length > 0 && (
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
          <div className="text-sm text-gray-600">
            Showing <span className="font-semibold">{indexOfFirstRow + 1}</span> to{' '}
            <span className="font-semibold">
              {Math.min(indexOfLastRow, filteredCompanies.length)}
            </span>{' '}
            of <span className="font-semibold">{filteredCompanies.length}</span> corporate companies
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Previous
            </button>
            <div className="flex gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-10 h-10 rounded-lg transition ${
                      currentPage === pageNum
                        ? 'bg-black text-white'
                        : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add Company Modal - Clean */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FaBuilding className="text-2xl text-gray-800" />
                  <h2 className="text-2xl font-semibold text-gray-900">Create Company Account</h2>
                </div>
                <button
                  onClick={() => setShowAddModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Corporate ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyid"
                    placeholder="Enter Corporate ID (e.g., ACME001)"
                    value={newCompany.companyid}
                    onChange={handleInputChange}
                    className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
                      errors.companyid ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.companyid && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <FaInfoCircle className="text-xs" /> {errors.companyid}
                    </p>
                  )}
                  {!errors.companyid && newCompany.companyid && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                      <FaCheckCircle className="text-xs" /> Valid Corporate ID
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyname"
                    placeholder="Enter Company Name (e.g., ACME Corporation)"
                    value={newCompany.companyname}
                    onChange={handleInputChange}
                    className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      errors.companyname ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {errors.companyname && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <FaInfoCircle className="text-xs" /> {errors.companyname}
                    </p>
                  )}
                  {!errors.companyname && newCompany.companyname && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                      <FaCheckCircle className="text-xs" /> Valid Company Name
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Company Address</label>
                  <textarea
                    name="companyaddress"
                    rows="2"
                    placeholder="Enter Company Address"
                    value={newCompany.companyaddress}
                    onChange={handleInputChange}
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="companyemail"
                      placeholder="Enter Email Address"
                      value={newCompany.companyemail}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="companyphone"
                      placeholder="Enter Phone Number"
                      value={newCompany.companyphone}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bank Account Number</label>
                  <div className="relative">
                    <FaCreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="accountnumber"
                      placeholder="Enter Account Number"
                      value={newCompany.accountnumber}
                      onChange={handleInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    GST Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaRegFileAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="gstnumber"
                      placeholder="Enter GST Number (e.g., 22AAAAA0000A1Z5)"
                      value={newCompany.gstnumber}
                      onChange={handleInputChange}
                      className={`w-full pl-10 pr-3 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
                        errors.gstnumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>
                  {errors.gstnumber && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <FaInfoCircle className="text-xs" /> {errors.gstnumber}
                    </p>
                  )}
                  {!errors.gstnumber && newCompany.gstnumber && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                      <FaCheckCircle className="text-xs" /> Valid GST Number
                    </p>
                  )}
                </div>

                <div className="md:col-span-2 border-t pt-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Contract Information</h3>
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      name="has_contract"
                      checked={newCompany.has_contract}
                      onChange={handleInputChange}
                      className="w-4 h-4 text-black rounded focus:ring-black"
                    />
                    <span className="text-sm text-gray-700">Has Active Contract</span>
                  </label>

                  {newCompany.has_contract && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Contract ID</label>
                        <div className="relative">
                          <FaFileContract className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="contractid"
                            placeholder="Enter Contract ID (e.g., PECDIS_025)"
                            value={newCompany.contractid}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <FaInfoCircle className="text-gray-400" />
                          Examples: PECDIS_025 (25%)
                        </p>
                      </div>
                      {newCompany.discount_percentage && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                          <FaPercent className="text-green-600" />
                          <span className="text-sm text-green-700">
                            {newCompany.discount_percentage}% discount will be applied
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  Create Company Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Company Modal - Clean */}
      {showEditModal && editCompany && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FaEdit className="text-2xl text-gray-800" />
                  <h2 className="text-2xl font-semibold text-gray-900">Edit Company Account</h2>
                </div>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            <form onSubmit={handleEditSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Corporate ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={editCompany.companyid}
                    className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 font-mono"
                    disabled
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    Company Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="companyname"
                    placeholder="Enter Company Name (e.g., ACME Corporation)"
                    value={editCompany.companyname}
                    onChange={handleEditInputChange}
                    className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black ${
                      editErrors.companyname ? 'border-red-500' : 'border-gray-300'
                    }`}
                    required
                  />
                  {editErrors.companyname && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <FaInfoCircle className="text-xs" /> {editErrors.companyname}
                    </p>
                  )}
                  {!editErrors.companyname && editCompany.companyname && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                      <FaCheckCircle className="text-xs" /> Valid Company Name
                    </p>
                  )}
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm text-gray-700 mb-1">Company Address</label>
                  <textarea
                    name="companyaddress"
                    rows="2"
                    value={editCompany.companyaddress}
                    onChange={handleEditInputChange}
                    className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Email Address</label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="email"
                      name="companyemail"
                      value={editCompany.companyemail}
                      onChange={handleEditInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Phone Number</label>
                  <div className="relative">
                    <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="tel"
                      name="companyphone"
                      value={editCompany.companyphone}
                      onChange={handleEditInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">Bank Account Number</label>
                  <div className="relative">
                    <FaCreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="accountnumber"
                      value={editCompany.accountnumber}
                      onChange={handleEditInputChange}
                      className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-700 mb-1">
                    GST Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <FaRegFileAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      name="gstnumber"
                      placeholder="Enter GST Number (e.g., 22AAAAA0000A1Z5)"
                      value={editCompany.gstnumber || ''}
                      onChange={handleEditInputChange}
                      className={`w-full pl-10 pr-3 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
                        editErrors.gstnumber ? 'border-red-500' : 'border-gray-300'
                      }`}
                      required
                    />
                  </div>
                  {editErrors.gstnumber && (
                    <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                      <FaInfoCircle className="text-xs" /> {editErrors.gstnumber}
                    </p>
                  )}
                  {!editErrors.gstnumber && editCompany.gstnumber && (
                    <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
                      <FaCheckCircle className="text-xs" /> Valid GST Number
                    </p>
                  )}
                </div>

                <div className="md:col-span-2 border-t pt-4">
                  <h3 className="text-md font-semibold text-gray-900 mb-3">Contract Information</h3>
                  <label className="flex items-center gap-2 cursor-pointer mb-4">
                    <input
                      type="checkbox"
                      name="has_contract"
                      checked={editCompany.has_contract}
                      onChange={handleEditInputChange}
                      className="w-4 h-4 text-black rounded focus:ring-black"
                    />
                    <span className="text-sm text-gray-700">Has Active Contract</span>
                  </label>

                  {editCompany.has_contract && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Contract ID</label>
                        <div className="relative">
                          <FaFileContract className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            name="contractid"
                            placeholder="Enter Contract ID (e.g., PECDIS_025)"
                            value={editCompany.contractid || ''}
                            onChange={handleEditInputChange}
                            className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                          <FaInfoCircle className="text-gray-400" />
                          Examples: PECDIS_025 (25%)
                        </p>
                      </div>
                      {editCompany.discount_percentage && (
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                          <FaPercent className="text-green-600" />
                          <span className="text-sm text-green-700">
                            {editCompany.discount_percentage}% discount will be applied
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-6 border-t mt-6">
                <button
                  type="button"
                  onClick={() => setShowEditModal(false)}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition"
                >
                  Update Company Account
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal - Clean */}
      {showDeleteModal && itemToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <FaTrash className="text-red-600 text-lg" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Delete Company Account</h2>
            </div>
            <p className="text-gray-700 mb-2">
              Are you sure you want to delete the company account for{' '}
              <span className="font-semibold">{itemToDelete.companyname}</span>?
            </p>
            <p className="text-sm text-gray-500 mb-4">
              Corporate ID: <span className="font-mono">{itemToDelete.companyid}</span>
            </p>
            <p className="text-sm text-red-600 font-semibold mb-6">
              This action cannot be undone and will permanently remove all company data.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                Delete Company
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default AccountManagement;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import {
//   FaEdit,
//   FaTrash,
//   FaSearch,
//   FaBuilding,
//   FaPhone,
//   FaEnvelope,
//   FaMapMarkerAlt,
//   FaIdCard,
//   FaCheckCircle,
//   FaUsers,
//   FaUser,
//   FaTimes,
//   FaCreditCard,
//   FaFileContract,
//   FaRegFileAlt,
//   FaChartLine,
//   FaShieldAlt,
//   FaMoneyBillWave,
//   FaHandshake,
//   FaPlus,
//   FaEye,
//   FaEyeSlash,
//   FaInfoCircle,
//   FaTag,
//   FaPercent,
// } from 'react-icons/fa';
// import { ToastContainer, toast } from 'react-toastify';
// import 'react-toastify/dist/ReactToastify.css';

// const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

// const AccountManagement = () => {
//   const [companies, setCompanies] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [showAddModal, setShowAddModal] = useState(false);
//   const [showEditModal, setShowEditModal] = useState(false);
//   const [newCompany, setNewCompany] = useState({
//     companyid: '',
//     companyname: '',
//     companyaddress: '',
//     companyemail: '',
//     companyphone: '',
//     accountnumber: '',
//     is_phone_verified: false,
//     is_companyid_verified: false,
//     has_contract: false,
//     contractid: '',
//     gstnumber: '',
//     discount_percentage: '',
//   });
//   const [editCompany, setEditCompany] = useState(null);
//   const [showDeleteModal, setShowDeleteModal] = useState(false);
//   const [itemToDelete, setItemToDelete] = useState(null);
//   const [currentPage, setCurrentPage] = useState(1);
//   const rowsPerPage = 20;
//   const [searchTerm, setSearchTerm] = useState('');
//   const [errors, setErrors] = useState({});
//   const [editErrors, setEditErrors] = useState({});
//   const navigate = useNavigate();

//   useEffect(() => {
//     const fetchData = async () => {
//       try {
//         setLoading(true);
//         const companiesResponse = await fetch(`${BQ_BASE_URL}/bq/api/companies`);
//         const companiesData = await companiesResponse.json();

//         if (companiesData && companiesData.companies) {
//           setCompanies(companiesData.companies);
//         } else {
//           toast.error('No corporate companies found.');
//         }
//       } catch (error) {
//         toast.error('Failed to fetch data.');
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchData();
//   }, []);

//   // Calculate statistics
//   const totalCompanies = companies.length;
//   const verifiedCompanies = companies.filter((company) => company.is_fully_verified).length;
//   const companiesWithContract = companies.filter((company) => company.has_contract).length;
//   const activeCompanies = companies.filter((company) => company.is_active !== false).length;

//   // Get discount percentage from contract ID
//   const getDiscountFromContractId = (contractId) => {
//     if (!contractId) return null;
//     const match = contractId.match(/PECDIS_(\d{2,3})/);
//     if (match) {
//       const discount = parseInt(match[1]);
//       if (discount >= 1 && discount <= 50) {
//         return discount;
//       }
//     }
//     return null;
//   };

//   // Validation - Only for GST, Company Name, Company ID
//   const validateGST = (gst) => {
//     if (!gst) return true;
//     const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}[Z]{1}[0-9A-Z]{1}$/;
//     return gstRegex.test(gst);
//   };

//   const validateCompanyID = (companyId) => {
//     if (!companyId) return true;
//     // Check for capital letters only
//     const capitalRegex = /^[A-Z0-9]+$/;
//     if (!capitalRegex.test(companyId)) {
//       return 'must_be_capital';
//     }
//     if (companyId.length < 3 || companyId.length > 20) {
//       return 'length_error';
//     }
//     return true;
//   };

//   const validateCompanyName = (name) => {
//     if (!name) return true;
//     // Check if all letters are capital
//     const capitalRegex = /^[A-Z\s]+$/;
//     if (!capitalRegex.test(name)) {
//       return 'must_be_capital';
//     }
//     if (name.length < 2 || name.length > 100) {
//       return 'length_error';
//     }
//     return true;
//   };

//   const handleInputChange = (e) => {
//     const { name, value, type, checked } = e.target;

//     // Convert to uppercase for specific fields
//     let processedValue = value;
//     if (name === 'companyid' || name === 'companyname' || name === 'gstnumber') {
//       processedValue = value.toUpperCase();
//     }

//     setNewCompany((prev) => ({
//       ...prev,
//       [name]: type === 'checkbox' ? checked : processedValue,
//     }));

//     // Live validation errors
//     if (name === 'companyid') {
//       if (!processedValue || processedValue.trim() === '') {
//         setErrors((prev) => ({ ...prev, [name]: 'Corporate ID is required' }));
//       } else if (!/^[A-Z0-9]+$/.test(processedValue)) {
//         setErrors((prev) => ({
//           ...prev,
//           [name]: 'Corporate ID must contain only capital letters and numbers',
//         }));
//       } else if (processedValue.length < 3 || processedValue.length > 20) {
//         setErrors((prev) => ({ ...prev, [name]: 'Corporate ID must be 3-20 characters' }));
//       } else {
//         setErrors((prev) => ({ ...prev, [name]: '' }));
//       }
//     }

//     if (name === 'companyname') {
//       if (!processedValue || processedValue.trim() === '') {
//         setErrors((prev) => ({ ...prev, [name]: 'Company Name is required' }));
//       } else if (!/^[A-Z\s]+$/.test(processedValue)) {
//         setErrors((prev) => ({
//           ...prev,
//           [name]: 'Company Name must contain only capital letters and spaces',
//         }));
//       } else if (processedValue.length < 2 || processedValue.length > 100) {
//         setErrors((prev) => ({ ...prev, [name]: 'Company Name must be 2-100 characters' }));
//       } else {
//         setErrors((prev) => ({ ...prev, [name]: '' }));
//       }
//     }

//     if (name === 'gstnumber') {
//       if (!processedValue || processedValue.trim() === '') {
//         setErrors((prev) => ({ ...prev, [name]: 'GST Number is required' }));
//       } else if (!validateGST(processedValue)) {
//         setErrors((prev) => ({ ...prev, [name]: 'Invalid GST format. Example: 22AAAAA0000A1Z5' }));
//       } else {
//         setErrors((prev) => ({ ...prev, [name]: '' }));
//       }
//     }

//     if (name === 'contractid') {
//       const discount = getDiscountFromContractId(processedValue);
//       setNewCompany((prev) => ({
//         ...prev,
//         discount_percentage: discount || '',
//       }));
//     }

//     if (errors[name] && name !== 'companyid' && name !== 'companyname' && name !== 'gstnumber') {
//       setErrors((prev) => ({ ...prev, [name]: '' }));
//     }
//   };

//   const handleEditInputChange = (e) => {
//     const { name, value, type, checked } = e.target;

//     // Convert to uppercase for specific fields
//     let processedValue = value;
//     if (name === 'companyname' || name === 'gstnumber') {
//       processedValue = value.toUpperCase();
//     }

//     setEditCompany((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : processedValue }));

//     // Live validation errors for edit
//     if (name === 'companyname') {
//       if (!processedValue || processedValue.trim() === '') {
//         setEditErrors((prev) => ({ ...prev, [name]: 'Company Name is required' }));
//       } else if (!/^[A-Z\s]+$/.test(processedValue)) {
//         setEditErrors((prev) => ({
//           ...prev,
//           [name]: 'Company Name must contain only capital letters and spaces',
//         }));
//       } else if (processedValue.length < 2 || processedValue.length > 100) {
//         setEditErrors((prev) => ({ ...prev, [name]: 'Company Name must be 2-100 characters' }));
//       } else {
//         setEditErrors((prev) => ({ ...prev, [name]: '' }));
//       }
//     }

//     if (name === 'gstnumber') {
//       if (!processedValue || processedValue.trim() === '') {
//         setEditErrors((prev) => ({ ...prev, [name]: 'GST Number is required' }));
//       } else if (!validateGST(processedValue)) {
//         setEditErrors((prev) => ({
//           ...prev,
//           [name]: 'Invalid GST format. Example: 22AAAAA0000A1Z5',
//         }));
//       } else {
//         setEditErrors((prev) => ({ ...prev, [name]: '' }));
//       }
//     }

//     if (name === 'contractid') {
//       const discount = getDiscountFromContractId(processedValue);
//       setEditCompany((prev) => ({
//         ...prev,
//         discount_percentage: discount || '',
//       }));
//     }

//     if (editErrors[name] && name !== 'companyname' && name !== 'gstnumber') {
//       setEditErrors((prev) => ({ ...prev, [name]: '' }));
//     }
//   };

//   const validateForm = (formData) => {
//     const newErrors = {};

//     // Check for empty required fields
//     if (!formData.companyid || formData.companyid.trim() === '') {
//       newErrors.companyid = 'Corporate ID is required';
//     } else if (!/^[A-Z0-9]+$/.test(formData.companyid)) {
//       newErrors.companyid = 'Corporate ID must contain only capital letters and numbers';
//     } else if (formData.companyid.length < 3 || formData.companyid.length > 20) {
//       newErrors.companyid = 'Corporate ID must be 3-20 characters';
//     }

//     if (!formData.companyname || formData.companyname.trim() === '') {
//       newErrors.companyname = 'Company Name is required';
//     } else if (!/^[A-Z\s]+$/.test(formData.companyname)) {
//       newErrors.companyname = 'Company Name must contain only capital letters and spaces';
//     } else if (formData.companyname.length < 2 || formData.companyname.length > 100) {
//       newErrors.companyname = 'Company Name must be 2-100 characters';
//     }

//     if (!formData.gstnumber || formData.gstnumber.trim() === '') {
//       newErrors.gstnumber = 'GST Number is required';
//     } else if (!validateGST(formData.gstnumber)) {
//       newErrors.gstnumber = 'Invalid GST format. Example: 22AAAAA0000A1Z5';
//     }

//     return newErrors;
//   };

//   const handleSubmit = async (e) => {
//     e.preventDefault();

//     const formErrors = validateForm(newCompany);
//     if (Object.keys(formErrors).length > 0) {
//       setErrors(formErrors);

//       // Show specific toast messages for each error
//       if (formErrors.companyid) {
//         toast.error(formErrors.companyid);
//       } else if (formErrors.companyname) {
//         toast.error(formErrors.companyname);
//       } else if (formErrors.gstnumber) {
//         toast.error(formErrors.gstnumber);
//       } else {
//         toast.error('Please fix the errors in the form');
//       }
//       return;
//     }

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/company`, {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(newCompany),
//       });

//       const data = await response.json();

//       if (data.message === 'Company created successfully') {
//         toast.success('Corporate company account created successfully!');
//         setCompanies((prevCompanies) => [...prevCompanies, data.company]);
//         setShowAddModal(false);
//         setNewCompany({
//           companyid: '',
//           companyname: '',
//           companyaddress: '',
//           companyemail: '',
//           companyphone: '',
//           accountnumber: '',
//           is_phone_verified: false,
//           is_companyid_verified: false,
//           has_contract: false,
//           contractid: '',
//           gstnumber: '',
//           discount_percentage: '',
//         });
//         setErrors({});
//       } else {
//         toast.error(data.message || 'Failed to create corporate account.');
//       }
//     } catch (error) {
//       toast.error('Error submitting data.');
//     }
//   };

//   const handleEditSubmit = async (e) => {
//     e.preventDefault();

//     const formErrors = validateForm(editCompany);
//     if (Object.keys(formErrors).length > 0) {
//       setEditErrors(formErrors);

//       // Show specific toast messages for each error
//       if (formErrors.companyid) {
//         toast.error(formErrors.companyid);
//       } else if (formErrors.companyname) {
//         toast.error(formErrors.companyname);
//       } else if (formErrors.gstnumber) {
//         toast.error(formErrors.gstnumber);
//       } else {
//         toast.error('Please fix the errors in the form');
//       }
//       return;
//     }

//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/company/${editCompany.companyid}`, {
//         method: 'PUT',
//         headers: {
//           'Content-Type': 'application/json',
//           Accept: 'application/json',
//         },
//         body: JSON.stringify(editCompany),
//       });

//       const data = await response.json();

//       if (data.message === 'Company updated successfully') {
//         toast.success('Corporate company account updated successfully!');
//         setCompanies((prevCompanies) =>
//           prevCompanies.map((company) =>
//             company.companyid === editCompany.companyid ? data.company : company
//           )
//         );
//         setShowEditModal(false);
//         setEditErrors({});
//       } else {
//         toast.error(data.message || 'Failed to update corporate account.');
//       }
//     } catch (error) {
//       toast.error('Error updating data.');
//     }
//   };

//   const handleEditClick = (company) => {
//     setEditCompany(company);
//     setEditErrors({});
//     setShowEditModal(true);
//   };

//   const handleDelete = async () => {
//     try {
//       const response = await fetch(`${BQ_BASE_URL}/bq/api/company/${itemToDelete.companyid}`, {
//         method: 'DELETE',
//         headers: {
//           Accept: 'application/json',
//         },
//       });

//       const data = await response.json();

//       if (data.message === 'Company deleted successfully') {
//         toast.success('Corporate company account deleted successfully!');
//         setCompanies((prevCompanies) =>
//           prevCompanies.filter((company) => company.companyid !== itemToDelete.companyid)
//         );
//       } else {
//         toast.error(data.message || 'Failed to delete corporate account.');
//       }
//       setShowDeleteModal(false);
//     } catch (error) {
//       toast.error('Error deleting item.');
//     }
//   };

//   const filteredCompanies = companies.filter(
//     (company) =>
//       company.companyid?.toLowerCase().includes(searchTerm.toLowerCase()) ||
//       company.companyname?.toLowerCase().includes(searchTerm.toLowerCase())
//   );

//   const totalPages = Math.ceil(filteredCompanies.length / rowsPerPage);
//   const indexOfLastRow = currentPage * rowsPerPage;
//   const indexOfFirstRow = indexOfLastRow - rowsPerPage;
//   const currentRows = filteredCompanies.slice(indexOfFirstRow, indexOfLastRow);

//   useEffect(() => {
//     setCurrentPage(1);
//   }, [searchTerm]);

//   return (
//     <div className="p-4 md:p-6 bg-gray-100 min-h-screen w-full overflow-x-hidden">
//       {/* Header Navigation - KEPT AS IS */}
//       <div className="flex justify-between items-center mb-6 bg-white p-3 rounded-lg shadow-sm">
//         <h1 className="text-black text-xl font-bold">Customer Management</h1>
//         <div className="flex space-x-4">
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/corporate-overview')}
//             className="text-gray-600 hover:text-black transition"
//           >
//             Overview
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/leisure')}
//             className="text-gray-600 hover:text-black transition"
//           >
//             Guest Management
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/corporate')}
//             className="text-black font-bold"
//           >
//             Corporate Accounts
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/contracts')}
//             className="text-gray-600 hover:text-black transition"
//           >
//             Contracts
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/credit-verification')}
//             className="text-gray-600 hover:text-black transition"
//           >
//             Credit & Verification
//           </button>
//           <button
//             onClick={() => navigate('/dashboard/mdm/customer-management/settings')}
//             className="text-gray-600 hover:text-black transition"
//           >
//             Settings
//           </button>
//         </div>
//       </div>

//       {/* Title Section - KEPT AS IS */}
//       <div className="mb-8">
//         <div className="flex flex-col items-center justify-center text-center space-y-2">
//           <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
//             Corporate Company Management
//           </h1>
//           <p className="text-gray-600 text-sm max-w-2xl">
//             Manage corporate company accounts for employee hotel stays and pricing agreements
//           </p>
//         </div>
//       </div>

//       {/* Stats Cards - Clean & Simple */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
//         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Total Companies</p>
//               <p className="text-2xl font-bold text-gray-900">{totalCompanies}</p>
//             </div>
//             <div className="p-2 bg-blue-100 rounded-lg">
//               <FaBuilding className="text-blue-600 text-lg" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Fully Verified</p>
//               <p className="text-2xl font-bold text-gray-900">{verifiedCompanies}</p>
//             </div>
//             <div className="p-2 bg-green-100 rounded-lg">
//               <FaShieldAlt className="text-green-600 text-lg" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Active Contracts</p>
//               <p className="text-2xl font-bold text-gray-900">{companiesWithContract}</p>
//             </div>
//             <div className="p-2 bg-purple-100 rounded-lg">
//               <FaHandshake className="text-purple-600 text-lg" />
//             </div>
//           </div>
//         </div>

//         <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500">Active Companies</p>
//               <p className="text-2xl font-bold text-gray-900">{activeCompanies}</p>
//             </div>
//             <div className="p-2 bg-emerald-100 rounded-lg">
//               <FaChartLine className="text-emerald-600 text-lg" />
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* Search and Add Button */}
//       <div className="flex flex-col md:flex-row items-center gap-4 mb-6">
//         <div className="relative w-full">
//           <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//           <input
//             type="text"
//             placeholder="Search by Corporate ID or Company Name..."
//             className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent bg-white"
//             value={searchTerm}
//             onChange={(e) => setSearchTerm(e.target.value)}
//           />
//         </div>

//         <button
//           className="bg-black text-white px-6 py-2.5 rounded-lg hover:bg-gray-800 transition duration-200 font-semibold flex items-center gap-2 shadow-sm whitespace-nowrap"
//           onClick={() => {
//             setShowAddModal(true);
//             setErrors({});
//           }}
//         >
//           <FaPlus className="text-sm" />
//           <span>Add Corporate Company</span>
//         </button>
//       </div>

//       {/* Table */}
//       <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
//         <div className="overflow-x-auto">
//           <table className="w-full text-sm">
//             <thead className="bg-gray-50 border-b border-gray-200">
//               <tr>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Corporate ID
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Company Name
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Contact
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   GST
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Contract
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Status
//                 </th>
//                 <th className="px-4 py-3 text-left text-xs text-gray-700 uppercase tracking-wider">
//                   Actions
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {loading ? (
//                 <tr>
//                   <td colSpan="7" className="text-center py-12">
//                     <div className="flex justify-center items-center">
//                       <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
//                       <span className="ml-3 text-gray-600">Loading corporate companies...</span>
//                     </div>
//                   </td>
//                 </tr>
//               ) : currentRows.length === 0 ? (
//                 <tr>
//                   <td colSpan="7" className="text-center py-12 text-gray-500">
//                     <FaBuilding className="mx-auto text-4xl text-gray-300 mb-3" />
//                     <p>No corporate companies found</p>
//                     {searchTerm && (
//                       <p className="text-sm mt-1">Try adjusting your search criteria</p>
//                     )}
//                   </td>
//                 </tr>
//               ) : (
//                 currentRows.map((company, index) => (
//                   <tr key={index} className="hover:bg-gray-50 transition duration-150">
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <span className="text-xs font-mono bg-gray-100 px-2 py-1 rounded">
//                         {company.companyid}
//                       </span>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="flex items-center gap-2">
//                         <FaBuilding className="text-gray-400 text-sm" />
//                         <span className="text-gray-900">{company.companyname}</span>
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="space-y-0.5">
//                         <div className="flex items-center gap-1 text-xs text-gray-600">
//                           <FaEnvelope className="text-gray-400 text-xs" />
//                           <span>{company.companyemail}</span>
//                         </div>
//                         <div className="flex items-center gap-1 text-xs text-gray-600">
//                           <FaPhone className="text-gray-400 text-xs" />
//                           <span>{company.companyphone}</span>
//                         </div>
//                       </div>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       {company.gstnumber ? (
//                         <span className="text-xs font-mono bg-blue-50 text-blue-700 px-2 py-1 rounded">
//                           {company.gstnumber}
//                         </span>
//                       ) : (
//                         <span className="text-sm text-gray-700">-</span>
//                       )}
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       {company.has_contract ? (
//                         <div>
//                           <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-green-100 text-green-800">
//                             <FaCheckCircle className="mr-1 text-xs" />
//                             Active
//                           </span>
//                           {company.contractid && (
//                             <span className="text-xs text-gray-500 block mt-0.5 font-mono">
//                               {company.contractid}
//                             </span>
//                           )}
//                         </div>
//                       ) : (
//                         <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
//                           No Contract
//                         </span>
//                       )}
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <span
//                         className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs ${
//                           company.is_fully_verified
//                             ? 'bg-green-100 text-green-800'
//                             : 'bg-yellow-100 text-yellow-800'
//                         }`}
//                       >
//                         {company.is_fully_verified ? 'Verified' : 'Pending'}
//                       </span>
//                     </td>
//                     <td className="px-4 py-3 whitespace-nowrap">
//                       <div className="flex items-center gap-2">
//                         <button
//                           onClick={() => handleEditClick(company)}
//                           className="text-gray-600 hover:text-black transition p-1.5 rounded hover:bg-gray-100"
//                           title="Edit Company"
//                         >
//                           <FaEdit size={16} />
//                         </button>
//                         <button
//                           onClick={() => {
//                             setItemToDelete(company);
//                             setShowDeleteModal(true);
//                           }}
//                           className="text-red-500 hover:text-red-700 transition p-1.5 rounded hover:bg-red-50"
//                           title="Delete Company"
//                         >
//                           <FaTrash size={16} />
//                         </button>
//                       </div>
//                     </td>
//                   </tr>
//                 ))
//               )}
//             </tbody>
//           </table>
//         </div>
//       </div>

//       {/* Pagination */}
//       {filteredCompanies.length > 0 && (
//         <div className="flex flex-col sm:flex-row justify-between items-center mt-6 gap-4">
//           <div className="text-sm text-gray-600">
//             Showing <span className="font-semibold">{indexOfFirstRow + 1}</span> to{' '}
//             <span className="font-semibold">
//               {Math.min(indexOfLastRow, filteredCompanies.length)}
//             </span>{' '}
//             of <span className="font-semibold">{filteredCompanies.length}</span> corporate companies
//           </div>
//           <div className="flex items-center gap-2">
//             <button
//               onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
//               disabled={currentPage === 1}
//               className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
//             >
//               Previous
//             </button>
//             <div className="flex gap-1">
//               {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
//                 let pageNum;
//                 if (totalPages <= 5) {
//                   pageNum = i + 1;
//                 } else if (currentPage <= 3) {
//                   pageNum = i + 1;
//                 } else if (currentPage >= totalPages - 2) {
//                   pageNum = totalPages - 4 + i;
//                 } else {
//                   pageNum = currentPage - 2 + i;
//                 }

//                 return (
//                   <button
//                     key={pageNum}
//                     onClick={() => setCurrentPage(pageNum)}
//                     className={`w-10 h-10 rounded-lg transition ${
//                       currentPage === pageNum
//                         ? 'bg-black text-white'
//                         : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
//                     }`}
//                   >
//                     {pageNum}
//                   </button>
//                 );
//               })}
//             </div>
//             <button
//               onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
//               disabled={currentPage === totalPages}
//               className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed text-sm"
//             >
//               Next
//             </button>
//           </div>
//         </div>
//       )}

//       {/* Add Company Modal - Clean */}
//       {showAddModal && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
//             <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-3">
//                   <FaBuilding className="text-2xl text-gray-800" />
//                   <h2 className="text-2xl font-semibold text-gray-900">Create Company Account</h2>
//                 </div>
//                 <button
//                   onClick={() => setShowAddModal(false)}
//                   className="text-gray-400 hover:text-gray-600 transition"
//                 >
//                   <FaTimes size={20} />
//                 </button>
//               </div>
//             </div>

//             <form onSubmit={handleSubmit} className="p-6">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     Corporate ID <span className="text-red-500">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     name="companyid"
//                     placeholder="Enter Corporate ID (e.g., ACME001)"
//                     value={newCompany.companyid}
//                     onChange={handleInputChange}
//                     className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
//                       errors.companyid ? 'border-red-500' : 'border-gray-300'
//                     }`}
//                     required
//                   />
//                   {errors.companyid && (
//                     <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
//                       <FaInfoCircle className="text-xs" /> {errors.companyid}
//                     </p>
//                   )}
//                   {!errors.companyid && newCompany.companyid && (
//                     <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
//                       <FaCheckCircle className="text-xs" /> Valid Corporate ID
//                     </p>
//                   )}
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     Company Name <span className="text-red-500">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     name="companyname"
//                     placeholder="Enter Company Name (e.g., ACME CORPORATION)"
//                     value={newCompany.companyname}
//                     onChange={handleInputChange}
//                     className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
//                       errors.companyname ? 'border-red-500' : 'border-gray-300'
//                     }`}
//                     required
//                   />
//                   {errors.companyname && (
//                     <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
//                       <FaInfoCircle className="text-xs" /> {errors.companyname}
//                     </p>
//                   )}
//                   {!errors.companyname && newCompany.companyname && (
//                     <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
//                       <FaCheckCircle className="text-xs" /> Valid Company Name
//                     </p>
//                   )}
//                 </div>

//                 <div className="md:col-span-2">
//                   <label className="block text-sm text-gray-700 mb-1">Company Address</label>
//                   <textarea
//                     name="companyaddress"
//                     rows="2"
//                     placeholder="Enter Company Address"
//                     value={newCompany.companyaddress}
//                     onChange={handleInputChange}
//                     className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Email Address</label>
//                   <div className="relative">
//                     <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="email"
//                       name="companyemail"
//                       placeholder="Enter Email Address"
//                       value={newCompany.companyemail}
//                       onChange={handleInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Phone Number</label>
//                   <div className="relative">
//                     <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="tel"
//                       name="companyphone"
//                       placeholder="Enter Phone Number"
//                       value={newCompany.companyphone}
//                       onChange={handleInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Bank Account Number</label>
//                   <div className="relative">
//                     <FaCreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="text"
//                       name="accountnumber"
//                       placeholder="Enter Account Number"
//                       value={newCompany.accountnumber}
//                       onChange={handleInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     GST Number <span className="text-red-500">*</span>
//                   </label>
//                   <div className="relative">
//                     <FaRegFileAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="text"
//                       name="gstnumber"
//                       placeholder="Enter GST Number (e.g., 22AAAAA0000A1Z5)"
//                       value={newCompany.gstnumber}
//                       onChange={handleInputChange}
//                       className={`w-full pl-10 pr-3 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
//                         errors.gstnumber ? 'border-red-500' : 'border-gray-300'
//                       }`}
//                       required
//                     />
//                   </div>
//                   {errors.gstnumber && (
//                     <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
//                       <FaInfoCircle className="text-xs" /> {errors.gstnumber}
//                     </p>
//                   )}
//                   {!errors.gstnumber && newCompany.gstnumber && (
//                     <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
//                       <FaCheckCircle className="text-xs" /> Valid GST Number
//                     </p>
//                   )}
//                 </div>

//                 <div className="md:col-span-2 border-t pt-4">
//                   <h3 className="text-md font-semibold text-gray-900 mb-3">Contract Information</h3>
//                   <label className="flex items-center gap-2 cursor-pointer mb-4">
//                     <input
//                       type="checkbox"
//                       name="has_contract"
//                       checked={newCompany.has_contract}
//                       onChange={handleInputChange}
//                       className="w-4 h-4 text-black rounded focus:ring-black"
//                     />
//                     <span className="text-sm text-gray-700">Has Active Contract</span>
//                   </label>

//                   {newCompany.has_contract && (
//                     <div className="space-y-3">
//                       <div>
//                         <label className="block text-sm text-gray-700 mb-1">Contract ID</label>
//                         <div className="relative">
//                           <FaFileContract className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                           <input
//                             type="text"
//                             name="contractid"
//                             placeholder="Enter Contract ID (e.g., PECDIS_025)"
//                             value={newCompany.contractid}
//                             onChange={handleInputChange}
//                             className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
//                           />
//                         </div>
//                         <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
//                           <FaInfoCircle className="text-gray-400" />
//                           Examples: PECDIS_025 (25%), PECDIS_030 (30%), PECDIS_045 (45%)
//                         </p>
//                       </div>
//                       {newCompany.discount_percentage && (
//                         <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
//                           <FaPercent className="text-green-600" />
//                           <span className="text-sm text-green-700">
//                             {newCompany.discount_percentage}% discount will be applied
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </div>

//               <div className="flex justify-end gap-3 pt-6 border-t mt-6">
//                 <button
//                   type="button"
//                   onClick={() => setShowAddModal(false)}
//                   className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition"
//                 >
//                   Create Company Account
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Edit Company Modal - Clean */}
//       {showEditModal && editCompany && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
//             <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
//               <div className="flex items-center justify-between">
//                 <div className="flex items-center gap-3">
//                   <FaEdit className="text-2xl text-gray-800" />
//                   <h2 className="text-2xl font-semibold text-gray-900">Edit Company Account</h2>
//                 </div>
//                 <button
//                   onClick={() => setShowEditModal(false)}
//                   className="text-gray-400 hover:text-gray-600 transition"
//                 >
//                   <FaTimes size={20} />
//                 </button>
//               </div>
//             </div>

//             <form onSubmit={handleEditSubmit} className="p-6">
//               <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     Corporate ID <span className="text-red-500">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     value={editCompany.companyid}
//                     className="w-full p-2.5 border border-gray-300 rounded-lg bg-gray-50 text-gray-500 font-mono"
//                     disabled
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     Company Name <span className="text-red-500">*</span>
//                   </label>
//                   <input
//                     type="text"
//                     name="companyname"
//                     placeholder="Enter Company Name (e.g., ACME CORPORATION)"
//                     value={editCompany.companyname}
//                     onChange={handleEditInputChange}
//                     className={`w-full p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
//                       editErrors.companyname ? 'border-red-500' : 'border-gray-300'
//                     }`}
//                     required
//                   />
//                   {editErrors.companyname && (
//                     <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
//                       <FaInfoCircle className="text-xs" /> {editErrors.companyname}
//                     </p>
//                   )}
//                   {!editErrors.companyname && editCompany.companyname && (
//                     <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
//                       <FaCheckCircle className="text-xs" /> Valid Company Name
//                     </p>
//                   )}
//                 </div>

//                 <div className="md:col-span-2">
//                   <label className="block text-sm text-gray-700 mb-1">Company Address</label>
//                   <textarea
//                     name="companyaddress"
//                     rows="2"
//                     value={editCompany.companyaddress}
//                     onChange={handleEditInputChange}
//                     className="w-full p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black resize-none"
//                   />
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Email Address</label>
//                   <div className="relative">
//                     <FaEnvelope className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="email"
//                       name="companyemail"
//                       value={editCompany.companyemail}
//                       onChange={handleEditInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Phone Number</label>
//                   <div className="relative">
//                     <FaPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="tel"
//                       name="companyphone"
//                       value={editCompany.companyphone}
//                       onChange={handleEditInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">Bank Account Number</label>
//                   <div className="relative">
//                     <FaCreditCard className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="text"
//                       name="accountnumber"
//                       value={editCompany.accountnumber}
//                       onChange={handleEditInputChange}
//                       className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black"
//                     />
//                   </div>
//                 </div>

//                 <div>
//                   <label className="block text-sm text-gray-700 mb-1">
//                     GST Number <span className="text-red-500">*</span>
//                   </label>
//                   <div className="relative">
//                     <FaRegFileAlt className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                     <input
//                       type="text"
//                       name="gstnumber"
//                       placeholder="Enter GST Number (e.g., 22AAAAA0000A1Z5)"
//                       value={editCompany.gstnumber || ''}
//                       onChange={handleEditInputChange}
//                       className={`w-full pl-10 pr-3 p-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-black uppercase ${
//                         editErrors.gstnumber ? 'border-red-500' : 'border-gray-300'
//                       }`}
//                       required
//                     />
//                   </div>
//                   {editErrors.gstnumber && (
//                     <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
//                       <FaInfoCircle className="text-xs" /> {editErrors.gstnumber}
//                     </p>
//                   )}
//                   {!editErrors.gstnumber && editCompany.gstnumber && (
//                     <p className="text-green-500 text-xs mt-1 flex items-center gap-1">
//                       <FaCheckCircle className="text-xs" /> Valid GST Number
//                     </p>
//                   )}
//                 </div>

//                 <div className="md:col-span-2 border-t pt-4">
//                   <h3 className="text-md font-semibold text-gray-900 mb-3">Contract Information</h3>
//                   <label className="flex items-center gap-2 cursor-pointer mb-4">
//                     <input
//                       type="checkbox"
//                       name="has_contract"
//                       checked={editCompany.has_contract}
//                       onChange={handleEditInputChange}
//                       className="w-4 h-4 text-black rounded focus:ring-black"
//                     />
//                     <span className="text-sm text-gray-700">Has Active Contract</span>
//                   </label>

//                   {editCompany.has_contract && (
//                     <div className="space-y-3">
//                       <div>
//                         <label className="block text-sm text-gray-700 mb-1">Contract ID</label>
//                         <div className="relative">
//                           <FaFileContract className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
//                           <input
//                             type="text"
//                             name="contractid"
//                             placeholder="Enter Contract ID (e.g., PECDIS_025)"
//                             value={editCompany.contractid || ''}
//                             onChange={handleEditInputChange}
//                             className="w-full pl-10 pr-3 p-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black font-mono text-sm"
//                           />
//                         </div>
//                         <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
//                           <FaInfoCircle className="text-gray-400" />
//                           Examples: PECDIS_025 (25%), PECDIS_030 (30%), PECDIS_045 (45%)
//                         </p>
//                       </div>
//                       {editCompany.discount_percentage && (
//                         <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
//                           <FaPercent className="text-green-600" />
//                           <span className="text-sm text-green-700">
//                             {editCompany.discount_percentage}% discount will be applied
//                           </span>
//                         </div>
//                       )}
//                     </div>
//                   )}
//                 </div>
//               </div>

//               <div className="flex justify-end gap-3 pt-6 border-t mt-6">
//                 <button
//                   type="button"
//                   onClick={() => setShowEditModal(false)}
//                   className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
//                 >
//                   Cancel
//                 </button>
//                 <button
//                   type="submit"
//                   className="px-5 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition"
//                 >
//                   Update Company Account
//                 </button>
//               </div>
//             </form>
//           </div>
//         </div>
//       )}

//       {/* Delete Confirmation Modal - Clean */}
//       {showDeleteModal && itemToDelete && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
//           <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
//             <div className="flex items-center gap-3 mb-4">
//               <div className="p-2 bg-red-100 rounded-full">
//                 <FaTrash className="text-red-600 text-lg" />
//               </div>
//               <h2 className="text-xl font-semibold text-gray-900">Delete Company Account</h2>
//             </div>
//             <p className="text-gray-700 mb-2">
//               Are you sure you want to delete the company account for{' '}
//               <span className="font-semibold">{itemToDelete.companyname}</span>?
//             </p>
//             <p className="text-sm text-gray-500 mb-4">
//               Corporate ID: <span className="font-mono">{itemToDelete.companyid}</span>
//             </p>
//             <p className="text-sm text-red-600 font-semibold mb-6">
//               This action cannot be undone and will permanently remove all company data.
//             </p>
//             <div className="flex justify-end gap-3">
//               <button
//                 onClick={() => setShowDeleteModal(false)}
//                 className="px-5 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDelete}
//                 className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
//               >
//                 Delete Company
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       <ToastContainer
//         position="top-right"
//         autoClose={5000}
//         hideProgressBar={false}
//         closeOnClick
//         rtl={false}
//         pauseOnFocusLoss
//         draggable
//         pauseOnHover
//         theme="light"
//       />
//     </div>
//   );
// };

// export default AccountManagement;
